import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Skeleton} from '../../../../../../src/components/common/skeleton';
import {FolderBreadcrumb} from '../../../../../../src/components/folderBreadcrumbs/folderBreadcrumbs';
import {SettingsBreadcrumbs} from '../../../../../../src/settings/common/settingsBreadcrumbs';
import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../../shared/helpers/src/interaction/interactionHelpers';
import {fontWeights} from '../../../../../shared/ui/src/styles/fontStyles';

/*
 * Types.
 */

interface ContactsManagerHeaderProps extends InteractionComponentProps {
  title: ReactNode;
  contextDetails?: ReactNode;
  actions?: ReactNode;
  avatar?: ReactNode;

  topLevelBreadcrumb?: FolderBreadcrumb;
  isLoading?: boolean;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 32px 48px 12px 48px;
  flex-flow: column;
`;

const StyledTitleAndActionsContainerDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTitleContainerDiv = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledTitleDiv = styled.div`
  font-size: 24px;
  font-weight: ${fontWeights.black};
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledActionsContainerDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledBreadcrumbsContainerDiv = styled.div`
  margin: 0 0 5px 0;
`;

const StyledContextDetailsDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  margin: 0 0 3px 4px;
`;

const StyledAvatarDetailsDiv = styled.div`
  margin: 0 0 3px 8px;
`;

/*
 * Component.
 */

export const ContactsManagerHeader: FC<ContactsManagerHeaderProps> = (props) => {
  const {title, contextDetails, actions, avatar, topLevelBreadcrumb, isLoading, interactionId} = props;

  if (isLoading) {
    return (
      <StyledHeaderDiv>
        <StyledBreadcrumbsContainerDiv>
          <Skeleton height="12px" width="80px" />
        </StyledBreadcrumbsContainerDiv>
        <StyledTitleAndActionsContainerDiv>
          <StyledTitleContainerDiv>
            <Skeleton height="28px" width="250px" />
          </StyledTitleContainerDiv>
        </StyledTitleAndActionsContainerDiv>
      </StyledHeaderDiv>
    );
  }

  return (
    <StyledHeaderDiv {...buildInteractionIdProps(interactionId)}>
      {maybeRenderBreadcrumbs(topLevelBreadcrumb)}
      <StyledTitleAndActionsContainerDiv>
        <StyledTitleContainerDiv>
          <StyledTitleDiv>{title}</StyledTitleDiv>

          {avatar && <StyledAvatarDetailsDiv>{avatar}</StyledAvatarDetailsDiv>}
          {contextDetails && <StyledContextDetailsDiv>{contextDetails}</StyledContextDetailsDiv>}
        </StyledTitleContainerDiv>
        {actions && <StyledActionsContainerDiv>{actions}</StyledActionsContainerDiv>}
      </StyledTitleAndActionsContainerDiv>
    </StyledHeaderDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderBreadcrumbs(topLevelBreadcrumb?: FolderBreadcrumb) {
  if (!topLevelBreadcrumb) {
    return null;
  }
  return (
    <StyledBreadcrumbsContainerDiv>
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={topLevelBreadcrumb}
        breadcrumbs={[]}
      />
    </StyledBreadcrumbsContainerDiv>
  );
}
